import * as FileSaver from 'file-saver';
import ExcelJS from 'exceljs';
import type {
  IBreakdownCellHighlight,
  IBreakdownNoteRow,
  IFixedBreakdownRow,
  IStudioBreakdownExcelConfig,
  TBreakdownDataCol,
  TBreakdownInfoCol,
} from './types';
import {
  applyTableBorders,
  EXCEL_TYPE,
  INFO_COL_COUNT,
  resolveInfoCellStyle,
  STUDIO_COLUMN_FILL,
  styleActualForecastCell,
  styleDataCell,
  styleGreenHeaderCell,
  styleYearHeaderCell,
  UPDATE_TIME_FILL,
} from './styles';
import {
  buildDataCols,
  collectSortedMonths,
  formatCellValue,
  formatMonthLabel,
  groupMonthsByYear,
  sumValues,
} from './monthColumns';
import { resolveStudioBreakdownBlocks } from './resolveStudioBlocks';

const styleInfoCell = <TMonthRow>(
  cell: ExcelJS.Cell,
  column: TBreakdownInfoCol,
  value: string | null,
  highlights: IBreakdownCellHighlight[],
  fixedRow?: IFixedBreakdownRow<TMonthRow>,
): void => {
  const text = value ?? '';
  const ruleStyle = resolveInfoCellStyle(column, text, highlights);
  // Row-level style wins for duplicate labels (e.g. burn rate "R&D Cost");
  // cellHighlights still cover unique texts (Staff Rate / HC totals).
  const bold = fixedRow?.boldColumns?.includes(column) || ruleStyle.bold;
  const color = fixedRow?.columnColors?.[column] ?? ruleStyle.color;
  cell.value = value || null;
  styleDataCell(cell, 'left', bold, color);
};

const writeYearLabels = (
  row: ExcelJS.Row,
  dataCols: TBreakdownDataCol[],
): void => {
  dataCols.forEach((col, i) => {
    const cell = row.getCell(INFO_COL_COUNT + 1 + i);
    cell.value = col.type === 'month' ? col.month.slice(0, 4) : col.year;
    cell.font = { size: 10 };
    cell.alignment = { horizontal: 'center' };
  });
};

const writeNoteRow = (
  sheet: ExcelJS.Worksheet,
  rowDef: IBreakdownNoteRow,
  dataCols: TBreakdownDataCol[],
): void => {
  const row = sheet.addRow([]);
  row.height = 18;
  row.getCell(1).value = rowDef.text;
  row.getCell(1).font = {
    bold: rowDef.bold ?? false,
    size: 10,
    ...(rowDef.color ? { color: { argb: rowDef.color } } : {}),
  };
  // HC/Staff Rate put years on the note row; multi-note templates leave years for a later row.
  if (rowDef.trailingYears) {
    writeYearLabels(row, dataCols);
  }
};

const writeMetaRow = (
  sheet: ExcelJS.Worksheet,
  unitLabel: string | undefined,
  dataCols: TBreakdownDataCol[],
  datatime: string,
  // First matching month row across studios (Portfolio shares one Actual/Forecast band).
  resolveMonthRow: (month: string) => unknown | undefined,
  isActualMonth: (row: unknown) => boolean | null,
  getYearActualStatus: (year: string) => boolean | null,
): void => {
  const row = sheet.addRow([]);
  row.height = 18;
  const formattedDatatime = datatime ? datatime.replace('-', '/') : '';
  if (unitLabel) {
    const unitCell = row.getCell(1);
    unitCell.value = unitLabel;
    unitCell.font = { size: 10 };
    // Peach fill on unit cell only (column A); Update Time keeps its own fill in D.
    unitCell.fill = UPDATE_TIME_FILL;
    unitCell.alignment = { horizontal: 'left', vertical: 'middle' };
  }
  // Update Time sits in the last info column (D), before month columns begin.
  const updateTimeCell = row.getCell(INFO_COL_COUNT);
  updateTimeCell.value = `Update Time: ${formattedDatatime}`;
  updateTimeCell.font = { bold: true, size: 10 };
  updateTimeCell.fill = UPDATE_TIME_FILL;
  updateTimeCell.alignment = { horizontal: 'left', vertical: 'middle' };
  dataCols.forEach((col, i) => {
    const cell = row.getCell(INFO_COL_COUNT + 1 + i);
    if (col.type === 'month') {
      const monthRow = resolveMonthRow(col.month);
      styleActualForecastCell(cell, monthRow ? isActualMonth(monthRow) : null);
      return;
    }
    styleActualForecastCell(cell, getYearActualStatus(col.year));
  });
};

export const exportStudioBreakdownExcel = async <TMonthRow>(
  config: IStudioBreakdownExcelConfig<TMonthRow>,
): Promise<void> => {
  const {
    name,
    sheetName,
    noteRows,
    unitLabel,
    infoHeaders,
    datatime,
    getMonthKey,
    isActualMonth,
    cellHighlights = [],
    infoWidths,
  } = config;

  const studioBlocks = resolveStudioBreakdownBlocks(config);
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName);

  // Shared month axis across all studios (Portfolio may have uneven series coverage).
  const allMonthRows = studioBlocks.flatMap((block) => block.rawData);
  const sortedMonths = collectSortedMonths(allMonthRows, getMonthKey);
  const yearMap = groupMonthsByYear(sortedMonths);
  const dataCols = buildDataCols(yearMap);

  const resolveMonthRow = (month: string): TMonthRow | undefined => {
    for (const block of studioBlocks) {
      const match = block.rawData.find((row) => getMonthKey(row) === month);
      if (match) {
        return match;
      }
    }
    return undefined;
  };

  const getYearActualStatus = (year: string): boolean | null => {
    const yearMonths = yearMap.get(year) ?? [];
    if (!yearMonths.length) return null;
    const statuses = yearMonths.map((month) => {
      const monthRow = resolveMonthRow(month);
      return monthRow ? isActualMonth(monthRow) : null;
    });
    // Any missing flag → blank year-total label (do not default to Forecast).
    if (statuses.some((status) => status === null)) return null;
    // Year-total Actual only when every month in that year is Actual.
    return statuses.every((status) => status === true);
  };

  const yearKeys = Array.from(yearMap.keys());
  const yearIndexMap = new Map(yearKeys.map((y, i) => [y, i]));

  noteRows.forEach((note) => writeNoteRow(sheet, note, dataCols));

  // Burn Rate has 2+ notes so years cannot share row 1; insert a dedicated year row.
  // HC/Staff Rate already wrote years via trailingYears — skip to avoid a duplicate row.
  const hasTrailingYears = noteRows.some((note) => note.trailingYears);
  if (!hasTrailingYears) {
    const yearRow = sheet.addRow([]);
    yearRow.height = 18;
    writeYearLabels(yearRow, dataCols);
  }

  // Always append Update Time + Actual/Forecast; configs only declare noteRows / unitLabel.
  writeMetaRow(
    sheet,
    unitLabel,
    dataCols,
    datatime,
    resolveMonthRow,
    (row) => isActualMonth(row as TMonthRow),
    getYearActualStatus,
  );

  // notes + optional dedicated year row + meta row (before the green column-header row).
  const preambleRowCount = noteRows.length + (hasTrailingYears ? 0 : 1) + 1;

  const headerRow = sheet.addRow([]);
  headerRow.height = 22;
  infoHeaders.forEach((label, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = label;
    styleGreenHeaderCell(cell, 'left');
  });
  dataCols.forEach((col, i) => {
    const cell = headerRow.getCell(INFO_COL_COUNT + 1 + i);
    cell.value = col.type === 'month' ? formatMonthLabel(col.month) : 'Total';
    const yearIdx = yearIndexMap.get(
      col.type === 'month' ? col.month.slice(0, 4) : col.year,
    ) ?? 0;
    styleYearHeaderCell(cell, yearIdx);
  });

  studioBlocks.forEach((block) => {
    const monthLookup = new Map<string, TMonthRow>();
    block.rawData.forEach((row) => monthLookup.set(getMonthKey(row), row));

    block.fixedRows.forEach((fixedRow) => {
      const excelRow = sheet.addRow([]);
      excelRow.height = 16;

      const studioCell = excelRow.getCell(1);
      styleInfoCell(studioCell, 1, block.studioName, cellHighlights, fixedRow);
      studioCell.fill = STUDIO_COLUMN_FILL;

      styleInfoCell(excelRow.getCell(2), 2, fixedRow.level1, cellHighlights, fixedRow);
      styleInfoCell(
        excelRow.getCell(3),
        3,
        fixedRow.level2 || null,
        cellHighlights,
        fixedRow,
      );
      styleInfoCell(
        excelRow.getCell(4),
        4,
        fixedRow.level3 || null,
        cellHighlights,
        fixedRow,
      );

      dataCols.forEach((col, colIdx) => {
        const cell = excelRow.getCell(INFO_COL_COUNT + 1 + colIdx);

        if (col.type === 'total') {
          const yearMonths = yearMap.get(col.year) ?? [];
          const monthValues = yearMonths
            .map((m) => monthLookup.get(m))
            .filter((row): row is TMonthRow => !!row)
            .map((apiRow) => fixedRow.getValue(apiRow));
          // Year totals are client-side sums; API does not return a separate total field.
          cell.value = formatCellValue(sumValues(...monthValues));
          styleDataCell(cell, 'center');
          return;
        }

        const apiRow = monthLookup.get(col.month);
        if (!apiRow) {
          cell.value = '';
          styleDataCell(cell, 'center');
          return;
        }

        cell.value = formatCellValue(fixedRow.getValue(apiRow));
        styleDataCell(cell, 'center');
      });
    });
  });

  const TABLE_HEADER_ROW = preambleRowCount + 1;
  const totalCols = INFO_COL_COUNT + dataCols.length;
  applyTableBorders(sheet, TABLE_HEADER_ROW, sheet.rowCount, totalCols);

  // Info column widths come from each export config (not a shared default).
  infoWidths.forEach((w, i) => {
    sheet.getColumn(i + 1).width = w;
  });
  dataCols.forEach((col, i) => {
    sheet.getColumn(INFO_COL_COUNT + 1 + i).width = col.type === 'total' ? 10 : 8;
  });

  // Freeze A–D + preamble/header so month columns scroll horizontally under a fixed left pane.
  sheet.views = [{ state: 'frozen', xSplit: INFO_COL_COUNT, ySplit: TABLE_HEADER_ROW }];

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: EXCEL_TYPE });
  FileSaver.saveAs(blob, `${name}.xlsx`);
};
